import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { useTaskDetailSubscription } from "./useTaskDetailSubscription";
import { useTaskDetailActions } from "./useTaskDetailActions";
import { TaskInfoSection } from "./TaskInfoSection";
import { UrlUpdateSection } from "./UrlUpdateSection";
import { AnchorsSection } from "./AnchorsSection";
import { ArticleTopicsSection } from "./ArticleTopicsSection";
import { PublicationSection } from "./PublicationSection";
import { PublicationParamsSection } from "./PublicationParamsSection";
import { PublicationLaunchSection } from "./PublicationLaunchSection";
import { LogsSection } from "./LogsSection";
import { PreviewDialog } from "./PreviewDialog";
import { IfPermission } from "../../auth/IfPermission";
import type { JSX } from "react";

export function TaskDetail() {
  let taskDetail: JSX.Element;
  const { state } = useTaskDetailSubscription();
  const actions = useTaskDetailActions();
  const navigate = useNavigate();
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deactivateReason, setDeactivateReason] = useState("");
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  if (state.kind === "loading") {
    taskDetail = (
      <Box display="flex" justifyContent="center" alignItems="center" p={6}>
        <CircularProgress />
      </Box>
    );
  } else {
    if (state.kind === "error") {
      taskDetail = (
      <Box p={3}>
        <Alert severity="error">Ошибка подключения. Переподключение…</Alert>
      </Box>
    );
    } else {
      const isDeactivated = state.task.deactivatedAt !== null;
      const previewSiblings =
        actions.previewContext === null ? [] : actions.previewContext.siblings;
      const cloneFrom = {
        task: state.task,
        anchors: state.anchors,
        articleTopics: state.articleTopics,
      };
      function handleClone() {
        void navigate("/tasks/create", { state: { cloneFrom } });
      }
      function handleDeactivateConfirm() {
        actions.deactivate(deactivateReason || undefined);
        setDeactivateDialogOpen(false);
        setDeactivateReason("");
      }
      function handleActivateConfirm() {
        actions.activate();
        setActivateDialogOpen(false);
      }
      taskDetail = (
    <Box p={3}>
      <Box mb={2} display="flex" gap={1} alignItems="center">
        <Button startIcon={<ArrowBackIcon />} onClick={() => {
      void navigate("/tasks");
    }}>
          К списку задач
        </Button>
        <IfPermission permission="tasks.create">
          <Button variant="outlined" onClick={handleClone}>
            Клонировать
          </Button>
        </IfPermission>
      </Box>

      <TaskInfoSection task={state.task} />

      {state.publication !== null && (
        <PublicationParamsSection publication={state.publication} />
      )}

      {isDeactivated && (
        <Box mb={3} p={2} sx={{ bgcolor: "action.hover", borderRadius: 1, border: 1, borderColor: "divider" }}>
          <Typography variant="h6" mb={1}>
            Задача деактивирована
          </Typography>
          <Typography variant="body2">
            <strong>Дата:</strong> {new Date(state.task.deactivatedAt!).toLocaleString()}
          </Typography>
          {state.task.deactivatedByName !== null && (
            <Typography variant="body2">
              <strong>Кем:</strong> {state.task.deactivatedByName}
            </Typography>
          )}
          {state.task.deactivationReason !== null && (
            <Typography variant="body2">
              <strong>Причина:</strong> {state.task.deactivationReason}
            </Typography>
          )}
        </Box>
      )}

      <IfPermission permission="tasks.manage">
        <Box mb={2} display="flex" gap={1}>
          {!isDeactivated && state.publication === null && (
            <Button
              variant="outlined"
              color="warning"
              onClick={() => setDeactivateDialogOpen(true)}
              disabled={actions.deactivateState.kind === "loading"}
            >
              Деактивировать
            </Button>
          )}
          {isDeactivated && (
            <Button
              variant="outlined"
              color="success"
              onClick={() => setActivateDialogOpen(true)}
              disabled={actions.activateState.kind === "loading"}
            >
              Активировать
            </Button>
          )}
        </Box>
      </IfPermission>

      {actions.deactivateState.kind === "error" && (
        <Box mb={2}>
          <Alert severity="error">{actions.deactivateState.message}</Alert>
        </Box>
      )}
      {actions.activateState.kind === "error" && (
        <Box mb={2}>
          <Alert severity="error">{actions.activateState.message}</Alert>
        </Box>
      )}

      <IfPermission permission="generation.manage">
        <UrlUpdateSection
          currentUrl={state.task.targetUrl}
          onUpdateUrl={actions.updateUrl}
          state={actions.updateUrlState}
        />
      </IfPermission>

      {!isDeactivated && state.task.phase === "generation" && state.task.phaseStatus === "completed" && state.publication === null && (
        <IfPermission permission="publications.start">
          <PublicationLaunchSection
            taskType={state.task.taskType}
            onStartPublication={actions.startPublication}
            state={actions.startPublicationState}
          />
        </IfPermission>
      )}

      {state.publication && (
        <PublicationSection
          publication={state.publication}
          onStop={actions.stopPublication}
          stopState={actions.stopPublicationState}
          onResume={actions.resumePublication}
          resumeState={actions.resumePublicationState}
          onReschedule={actions.rescheduleDeferred}
          rescheduleState={actions.rescheduleDeferredState}
          onCancelDeferred={actions.cancelDeferred}
          cancelDeferredState={actions.cancelDeferredState}
          onRetryFailed={actions.retryFailedItems}
          retryFailedState={actions.retryFailedItemsState}
          onEnsureIndexing={actions.ensureIndexingSubmissions}
          ensureIndexingState={actions.ensureIndexingSubmissionsState}
          isDeactivated={isDeactivated}
          onExport={actions.exportPublicationResults}
          exportState={actions.exportState}
        />
      )}

      {actions.ensureIndexingSubmissionsState.kind === "success" && (
        <Box mb={2}>
          <Alert severity="success" onClose={actions.resetEnsureIndexingSubmissionsState}>
            Создано submissions: {actions.ensureIndexingSubmissionsState.createdCount}
          </Alert>
        </Box>
      )}

      {actions.ensureIndexingSubmissionsState.kind === "error" && (
        <Box mb={2}>
          <Alert severity="error" onClose={actions.resetEnsureIndexingSubmissionsState}>
            {actions.ensureIndexingSubmissionsState.message}
          </Alert>
        </Box>
      )}

      <LogsSection
        logs={state.logs}
        onDownloadLog={actions.downloadLog}
        downloadLogState={actions.downloadLogState}
      />

      {actions.restartUnfinishedUnitsState.kind === "success" && (
        <Box mb={2}>
          <Alert severity="info" onClose={actions.resetRestartUnfinishedUnitsState}>
            {actions.restartUnfinishedUnitsState.retriedCount === 0
              ? "Единиц, подлежащих перезапуску, нет."
              : `Перезапущено единиц: ${actions.restartUnfinishedUnitsState.retriedCount}`}
          </Alert>
        </Box>
      )}
      {actions.restartUnfinishedUnitsState.kind === "error" && (
        <Box mb={2}>
          <Alert severity="error" onClose={actions.resetRestartUnfinishedUnitsState}>
            {actions.restartUnfinishedUnitsState.message}
          </Alert>
        </Box>
      )}

      <AnchorsSection
        taskType={state.task.taskType}
        anchors={state.anchors}
        publicationItems={state.publication ? state.publication.items : null}
        onRegenerate={actions.regenerateAnchor}
        regenerateState={actions.regenerateAnchorState}
        onRestartUnfinished={actions.restartUnfinishedUnits}
        restartUnfinishedState={actions.restartUnfinishedUnitsState}
        onViewVariation={actions.viewVariationPreview}
      />

      <ArticleTopicsSection
        articleTopics={state.articleTopics}
        publicationItems={state.publication ? state.publication.items : null}
        onRegenerate={actions.regenerateAnchor}
        regenerateState={actions.regenerateAnchorState}
        onRestartUnfinished={actions.restartUnfinishedUnits}
        restartUnfinishedState={actions.restartUnfinishedUnitsState}
        onViewVariation={actions.viewVariationPreview}
      />

      {actions.previewState.kind !== "idle" && (
        <PreviewDialog
          state={actions.previewState}
          context={actions.previewContext}
          onClose={actions.closePreview}
          onSelectVariation={(id) => actions.viewVariationPreview(id, previewSiblings)}
        />
      )}

      <Dialog open={deactivateDialogOpen} onClose={() => setDeactivateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Деактивировать задачу</DialogTitle>
        <DialogContent>
          <TextField
            label="Причина (необязательно)"
            value={deactivateReason}
            onChange={(event) => setDeactivateReason(event.target.value)}
            fullWidth
            multiline
            rows={3}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleDeactivateConfirm} color="warning" variant="contained">
            Деактивировать
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={activateDialogOpen} onClose={() => setActivateDialogOpen(false)}>
        <DialogTitle>Активировать задачу</DialogTitle>
        <DialogContent>
          <Typography>Вы уверены, что хотите активировать задачу?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleActivateConfirm} color="success" variant="contained">
            Активировать
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
    }
  }
  return taskDetail;
}
